"use client";

import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";

interface BreakdownReturn {
  days: number;
  hh: string;
  mm: string;
  ss: string;
}

function breakdown(remainingSeconds: number): BreakdownReturn {
  const days = Math.floor(remainingSeconds / 86400);
  const hours = Math.floor((remainingSeconds % 86400) / 3600);
  const minutes = Math.floor((remainingSeconds % 3600) / 60);
  const seconds = remainingSeconds % 60;
  return {
    days,
    hh: String(hours).padStart(2, "0"),
    mm: String(minutes).padStart(2, "0"),
    ss: String(seconds).padStart(2, "0"),
  };
}

interface StatementCountdownProps {
  endsAt: string;
  isOpen: boolean;
}

/**
 * Voting-window banner on the statement detail page: a yellow-bordered card with
 * the "active for 2 weeks" note, the brand-yellow HH:MM:SS clock, and the
 * remaining-days line. Digits stay Latin to match the design — the clock is a
 * literal string and `days` is passed as a string so next-intl does not apply
 * the locale's (Persian) number format to it.
 */
export const StatementCountdown: React.FC<StatementCountdownProps> = ({
  endsAt,
  isOpen,
}) => {
  const t = useTranslations("statement");
  // Start as null so SSR and the first client render produce identical HTML
  // (a live Date.now() would differ across the SSR boundary → hydration error).
  // The real countdown is computed only after mount.
  const [now, setNow] = useState<number | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setNow(Math.floor(Date.now() / 1000));
    const interval = setInterval(() => {
      setNow(Math.floor(Date.now() / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [isOpen]);

  if (!isOpen) {
    return (
      <div className="rounded-2xl border border-stroke-light bg-bg-secondary px-4 py-5 text-center text-sm text-text-secondary">
        {t("vote.closed")}
      </div>
    );
  }

  const targetUnixSeconds = Math.floor(new Date(endsAt).getTime() / 1000);
  const parts =
    now === null ? null : breakdown(Math.max(0, targetUnixSeconds - now));

  return (
    <div className="rounded-2xl border border-accent px-4 py-5 text-center space-y-3">
      <p className="text-sm font-medium leading-relaxed text-text-primary">
        {t("detail.activeNote")}
      </p>
      <div className="flex items-center justify-center gap-4 text-accent">
        <span className="text-sm font-medium" suppressHydrationWarning>
          {parts ? t("detail.daysRemaining", { days: String(parts.days) }) : ""}
        </span>
        <bdi
          className="font-secondary text-2xl font-bold leading-none md:text-3xl"
          dir="ltr"
          suppressHydrationWarning
        >
          {parts ? `${parts.hh} : ${parts.mm} : ${parts.ss}` : "-- : -- : --"}
        </bdi>
      </div>
    </div>
  );
};
